import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { useGoogleMaps } from '../../GoogleMapsProvider/GoogleMapsProvider';

// Solo se piden los campos que realmente se usan (geometry/formatted_address/name):
// Basic Data. Sin esto, el widget de Autocomplete devuelve el Place completo
// (Contact Data + Atmosphere Data incluidos) en cada selección.
const AUTOCOMPLETE_FIELDS = ['geometry', 'formatted_address', 'name'];
const MAP_CONTAINER_STYLE = { width: '100%', height: '350px' };
const ECUADOR_CENTER = { lat: -1.8312, lng: -78.1834 };

/**
 * MapDireccionSelector
 *
 * Componente reutilizable para seleccionar una ubicación en Google Maps.
 *
 * Props:
 *  - latitud    {string}   Valor actual de latitud (geoX)
 *  - longitud   {string}   Valor actual de longitud (geoY)
 *  - direccion  {string}   Dirección actual
 *  - onSelect   {function} Callback ({ direccion, latitud, longitud }) al confirmar
 */
const MapDireccionSelector = ({ latitud = '', longitud = '', direccion = '', onSelect }) => {
    const [open, setOpen] = useState(false);
    const [tempMarker, setTempMarker] = useState(null);
    const [tempAddress, setTempAddress] = useState('');
    const [mapCenter, setMapCenter] = useState(ECUADOR_CENTER);
    const [geocoding, setGeocoding] = useState(false);

    const autocompleteRef = useRef(null);
    const geocoderRef = useRef(null);
    const mapRef = useRef(null);

    const { isLoaded, loadError } = useGoogleMaps();

    // Sincronizar estado interno al abrir el panel
    useEffect(() => {
        if (!open) return;
        const lat = parseFloat(latitud);
        const lng = parseFloat(longitud);
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            setTempMarker({ lat, lng });
            setMapCenter({ lat, lng });
            setTempAddress(direccion || '');
        } else {
            setTempMarker(null);
            setTempAddress('');
            setMapCenter(ECUADOR_CENTER);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const getGeocoder = useCallback(() => {
        if (!geocoderRef.current && window.google?.maps?.Geocoder) {
            geocoderRef.current = new window.google.maps.Geocoder();
        }
        return geocoderRef.current;
    }, []);

    const reverseGeocode = useCallback((lat, lng) => {
        const geocoder = getGeocoder();
        if (!geocoder) return;
        setGeocoding(true);
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            setGeocoding(false);
            if (status === 'OK' && results[0]) {
                setTempAddress(results[0].formatted_address);
            }
        });
    }, [getGeocoder]);

    const handleMapClick = useCallback((e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setTempMarker({ lat, lng });
        reverseGeocode(lat, lng);
    }, [reverseGeocode]);

    const handlePlaceChanged = useCallback(() => {
        if (!autocompleteRef.current) return;
        const place = autocompleteRef.current.getPlace();
        if (!place?.geometry?.location) return;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setTempMarker({ lat, lng });
        setMapCenter({ lat, lng });
        setTempAddress(place.formatted_address || place.name || '');
    }, []);

    const handleMyLocation = useCallback(() => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                setTempMarker({ lat, lng });
                setMapCenter({ lat, lng });
                reverseGeocode(lat, lng);
            },
            (err) => console.warn('Geolocation error:', err),
            { timeout: 8000 }
        );
    }, [reverseGeocode]);

    const handleConfirm = useCallback(() => {
        if (!tempMarker) return;
        onSelect({
            direccion: tempAddress,
            latitud: String(tempMarker.lat),
            longitud: String(tempMarker.lng),
        });
        setOpen(false);
    }, [tempMarker, tempAddress, onSelect]);

    const handleCancel = useCallback(() => {
        setOpen(false);
    }, []);

    const hasValue = latitud && longitud && parseFloat(latitud) !== 0;

    if (loadError) {
        return (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                Error al cargar Google Maps. Verifica la configuración de la API key.
            </p>
        );
    }

    return (
        <div className="mt-2">
            {/* ── Botón de apertura ── */}
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border-2 transition-all duration-200 ${
                    open
                        ? 'border-indigo-400 text-indigo-800 bg-indigo-100'
                        : hasValue
                        ? 'border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-400'
                        : 'border-indigo-200 text-indigo-600 bg-white hover:bg-indigo-50 hover:border-indigo-300'
                }`}
            >
                <LocationOnIcon sx={{ fontSize: 18 }} />
                {hasValue ? 'Actualizar ubicación en mapa' : 'Seleccionar ubicación en mapa'}
            </button>

            {/* ── Resumen de ubicación seleccionada (panel cerrado) ── */}
            {hasValue && !open && (
                <div className="mt-2 flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-sm">
                    <LocationOnIcon sx={{ fontSize: 15, color: '#4f46e5', flexShrink: 0, marginTop: '2px' }} />
                    <div className="min-w-0 flex-1">
                        {direccion && (
                            <p className="font-medium text-indigo-900 leading-snug break-words">{direccion}</p>
                        )}
                        <p className="text-xs text-indigo-500 font-mono mt-0.5">
                            {parseFloat(latitud).toFixed(6)}, {parseFloat(longitud).toFixed(6)}
                        </p>
                    </div>
                </div>
            )}

            {/* ── Panel del mapa ── */}
            {open && (
                <div className="mt-3 border-2 border-indigo-200 rounded-2xl overflow-hidden bg-white shadow-md">

                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-indigo-600">
                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                            <LocationOnIcon sx={{ fontSize: 18 }} />
                            <span>Selecciona la ubicación en el mapa</span>
                        </div>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="p-1 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </button>
                    </div>

                    {isLoaded ? (
                        <>
                            {/* Barra de búsqueda */}
                            <div className="flex gap-2 p-3 border-b border-indigo-100 bg-indigo-50">
                                <Autocomplete
                                    onLoad={(ref) => (autocompleteRef.current = ref)}
                                    onPlaceChanged={handlePlaceChanged}
                                    options={{ componentRestrictions: { country: 'ec' }, fields: AUTOCOMPLETE_FIELDS }}
                                    className="flex-1"
                                >
                                    <input
                                        type="text"
                                        placeholder="Buscar dirección o lugar..."
                                        className="w-full px-3 py-2 rounded-xl border-2 border-slate-200 text-sm bg-white transition-all duration-200 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                                    />
                                </Autocomplete>
                                <button
                                    type="button"
                                    onClick={handleMyLocation}
                                    title="Usar mi ubicación actual"
                                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-indigo-700 bg-white border-2 border-indigo-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-300 transition-colors whitespace-nowrap"
                                >
                                    <MyLocationIcon sx={{ fontSize: 16 }} />
                                    <span className="hidden sm:inline">Mi ubicación</span>
                                </button>
                            </div>

                            {/* Mapa */}
                            <div className="relative">
                                <GoogleMap
                                    mapContainerStyle={MAP_CONTAINER_STYLE}
                                    center={mapCenter}
                                    zoom={tempMarker ? 17 : 7}
                                    onClick={handleMapClick}
                                    onLoad={(map) => (mapRef.current = map)}
                                    options={{
                                        streetViewControl: false,
                                        mapTypeControl: true,
                                        fullscreenControl: false,
                                        clickableIcons: false,
                                    }}
                                >
                                    {tempMarker && (
                                        <Marker
                                            position={tempMarker}
                                            animation={window.google?.maps?.Animation?.DROP}
                                        />
                                    )}
                                </GoogleMap>

                                {/* Ayuda cuando no hay marcador */}
                                {!tempMarker && (
                                    <div className="absolute inset-0 flex items-end justify-center pb-6 pointer-events-none">
                                        <div className="bg-white/90 backdrop-blur-sm text-slate-600 text-xs font-medium px-4 py-2 rounded-full shadow border border-slate-200">
                                            Haz clic en el mapa para marcar la ubicación
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Dirección detectada */}
                            {tempMarker && (
                                <div className="px-4 py-3 bg-indigo-50 border-t border-indigo-100">
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        Dirección detectada
                                    </p>
                                    {geocoding ? (
                                        <div className="flex items-center gap-2 text-sm text-indigo-600">
                                            <CircularProgress size={12} sx={{ color: '#4f46e5' }} />
                                            <span>Obteniendo dirección...</span>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-indigo-900 font-medium leading-snug">
                                            {tempAddress || 'No se pudo obtener la dirección'}
                                        </p>
                                    )}
                                    <p className="text-xs text-indigo-500 font-mono mt-1">
                                        {tempMarker.lat.toFixed(6)}, {tempMarker.lng.toFixed(6)}
                                    </p>
                                </div>
                            )}

                            {/* Botones de acción */}
                            <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-white">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 rounded-xl border-2 border-slate-200 hover:bg-slate-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={!tempMarker || geocoding}
                                    className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-xl transition-all duration-200 ${
                                        tempMarker && !geocoding
                                            ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm active:scale-95'
                                            : 'bg-indigo-100 text-indigo-300 cursor-not-allowed'
                                    }`}
                                >
                                    <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                    Confirmar ubicación
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center gap-3 h-40 text-slate-400 text-sm">
                            <CircularProgress size={20} sx={{ color: '#6366f1' }} />
                            <span>Cargando mapa...</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MapDireccionSelector;
